import React, { useState, useEffect } from 'react';
// Fix: Use namespace import for react-router-dom to avoid missing exported member errors
import * as ReactRouterDOM from 'react-router-dom';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { Product } from '../../types';
import { ProductSaleType, ProductStatus, DeliveryMethod } from '../../types';
import { formatCurrency } from '../../utils/helpers';
import { useCart } from '../../contexts/CartContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import { StarRating } from './StarRating';

const { Link } = ReactRouterDOM as any;

const HeartIcon = ({ filled, className }: { filled: boolean, className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill={filled ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 6.364L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
    </svg>
);

const DefaultProductIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const FireIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
    </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
);

export const ProductCard: React.FC<{ product: Product; imageLoading?: 'eager' | 'lazy' }> = ({
    product,
    imageLoading = 'lazy',
}) => {
    const { t } = useTranslation();
    const { addToCart } = useCart();
    const { isFavorite, toggleFavorite } = useFavorites();
    const [isAdded, setIsAdded] = useState(false);
    
    // Image Handling State
    const [imageError, setImageError] = useState(false);
    const [logoError, setLogoError] = useState(false);

    useEffect(() => {
        setImageError(false);
        setLogoError(false);
    }, [product.image_url, product.logo_url]);
    
    const minQty = product.type_vente === ProductSaleType.GROS ? product.quantite_min : 1;
    const [quantity, setQuantity] = useState(minQty);

    const hasPreferences = product.preferences && product.preferences.length > 0;
    
    const favorite = isFavorite(product.id);
    
    const isPhysical = [DeliveryMethod.LIVRAISON_DOMICILE, DeliveryMethod.TRAITEMENT_LIEN].includes(product.delivery_method);
    const isPhysicalOutOfStock = isPhysical && (product.stock_quantity === 0);

    const isUnavailable = product.statut === ProductStatus.SOLD_OUT || product.statut === ProductStatus.INCOMING || isPhysicalOutOfStock;

    const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isUnavailable) {
            return;
        }

        if (quantity < minQty) {
            alert(t('product.alerts.min_qty', { min: minQty }));
            setQuantity(minQty);
            return;
        }

        if (quantity > 0) {
            addToCart(product, quantity, {});
            setIsAdded(true);
            setTimeout(() => {
                setIsAdded(false);
                setQuantity(minQty); 
            }, 2000);
        }
    };
    
    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        toggleFavorite(product.id);
    };
    
    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value)) {
            setQuantity(Math.max(minQty, value));
        } else {
            setQuantity(minQty);
        }
    };

    const renderBadge = () => {
        if (product.statut === ProductStatus.SOLD_OUT || isPhysicalOutOfStock) {
            return (
                <div className="absolute top-2 right-12 z-10 bg-red-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md border border-white/20 rtl:right-auto rtl:left-12">
                    {t('product.badges.sold_out')}
                </div>
            );
        }
        if (product.statut === ProductStatus.INCOMING) {
            return (
                <div className="absolute top-2 right-12 z-10 bg-gradient-to-r from-cyan-500 to-primary-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md flex items-center gap-1 border border-white/20 rtl:right-auto rtl:left-12">
                    <ClockIcon className="w-3 h-3" />
                    {t('product.badges.soon')}
                </div>
            );
        }
        if (product.est_meilleure_vente) {
            return (
                <div className="absolute top-2 right-12 z-10 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1 border border-white/20 animate-pulse-slow rtl:right-auto rtl:left-12">
                    <FireIcon className="w-3 h-3" />
                    {t('product.badges.best_seller')}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden group border border-gray-200 dark:border-gray-700 relative">
            <button 
                onClick={handleFavoriteClick} 
                className="absolute top-2 right-2 z-10 p-2 rounded-full bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-900 shadow-sm transition-colors rtl:right-auto rtl:left-2"
                aria-label={favorite ? t('product.card.remove_favorite') : t('product.card.add_favorite')}
                title={favorite ? t('product.card.remove_favorite') : t('product.card.add_favorite')}
            >
                <HeartIcon filled={favorite} className={`w-5 h-5 ${favorite ? 'text-red-500' : 'text-gray-400 dark:text-gray-500 hover:text-red-500'}`} />
            </button>

            <Link to={`/product/${product.id}`} className="block relative overflow-hidden bg-gray-100 dark:bg-gray-700">
                {product.image_url && !imageError ? (
                    <img 
                        src={product.image_url} 
                        alt={product.nom} 
                        crossOrigin="anonymous"
                        loading={imageLoading}
                        decoding="async"
                        className={`w-full h-40 object-cover transition-transform duration-300 ${isUnavailable ? 'grayscale opacity-70' : 'group-hover:scale-105'}`}
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="w-full h-40 flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-400">
                        <DefaultProductIcon />
                    </div>
                )}
                
                <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded-md backdrop-blur-sm rtl:left-auto rtl:right-2">
                    {product.sub_category_nom ? t(`subcategories.${product.sub_category_nom}`, { defaultValue: product.sub_category_nom }) : t('common.product')}
                </div>

                {renderBadge()}

                {product.type_vente === ProductSaleType.GROS && (
                     <div className="absolute bottom-2 left-2 bg-accent-600/90 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-sm backdrop-blur-sm border border-white/20 rtl:left-auto rtl:right-2">
                        {t('product.badges.wholesale', { min: product.quantite_min })}
                    </div>
                )}
            </Link>

            <div className="p-4 flex flex-col flex-grow">
                <div className="flex items-center gap-3 mb-2">
                    {product.logo_url && !logoError ? (
                        <img 
                            src={product.logo_url} 
                            alt="Logo" 
                            crossOrigin="anonymous"
                            loading="lazy"
                            decoding="async"
                            className="h-10 w-auto object-contain flex-shrink-0" 
                            onError={() => setLogoError(true)}
                        />
                    ) : (
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-100 dark:border-gray-600 p-1 text-xs font-bold text-gray-400">
                            {product.nom.substring(0,2).toUpperCase()}
                        </div>
                    )}
                    <h3 className="font-bold text-lg text-gray-800 dark:text-white leading-tight line-clamp-2">
                        <Link to={`/product/${product.id}`} className="hover:text-primary-600">
                            {t(`product_names.${product.nom}`, { defaultValue: product.nom })}
                        </Link>
                    </h3>
                </div>
                
                <div className="flex items-center mb-3">
                    <StarRating rating={product.rating_avg || 0} size="sm" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        ({product.rating_count || 0})
                    </span>
                </div>
                
                <div className="mt-auto pt-2">
                    <div className="flex items-baseline mb-4">
                        <p className="text-2xl font-extrabold text-primary-600 dark:text-primary-400">
                            {formatCurrency(product.prix)}
                        </p>
                    </div>

                    <div className="flex gap-2">
                        {isUnavailable ? (
                             <button disabled className="flex-grow w-full block bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed font-semibold py-2 px-3 rounded-md text-sm border border-gray-300 dark:border-gray-600">
                                {product.statut === ProductStatus.INCOMING ? t('product.soon_available') : t('product.out_of_stock')}
                            </button>
                        ) : hasPreferences ? (
                            <Link to={`/product/${product.id}`} className="flex-grow w-full text-center block bg-gray-800 dark:bg-gray-700 text-white font-semibold py-2 px-3 rounded-md hover:bg-gray-900 dark:hover:bg-gray-600 transition-colors duration-300 text-sm shadow-sm">
                                {t('product.see_options')}
                            </Link>
                        ) : (
                            <>
                                <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md">
                                    <button 
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuantity(q => Math.max(minQty, q - 1)); }}
                                        className="px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-md h-full rtl:rounded-l-none rtl:rounded-r-md"
                                        aria-label={t('product.card.decrease_qty')}
                                        type="button"
                                    >
                                        -
                                    </button>
                                    <input 
                                        type="number"
                                        value={quantity}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onChange={handleQuantityChange}
                                        className="w-12 text-center border-l border-r border-gray-300 dark:border-gray-600 bg-transparent focus:outline-none text-gray-900 dark:text-white"
                                        min={minQty}
                                        aria-label={t('common.quantity')}
                                    />
                                    <button 
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuantity(q => q + 1); }}
                                        className="px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-md h-full rtl:rounded-r-none rtl:rounded-l-md"
                                        aria-label={t('product.card.increase_qty')}
                                        type="button"
                                    >
                                        +
                                    </button>
                                </div>
                                <button 
                                    onClick={handleAddToCart}
                                    title={t('product.add_to_cart')}
                                    aria-label={t('product.add_to_cart')}
                                    className={clsx(
                                        "flex-grow flex items-center justify-center rounded-md transition-all duration-300 text-sm font-semibold px-3 py-2 shadow-sm",
                                        isAdded 
                                            ? 'bg-green-500 text-white' 
                                            : 'bg-primary-500 hover:bg-primary-400 text-white'
                                    )}
                                >
                                    {isAdded ? `${t('product.added')} ✓` : t('product.add')}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};